/**
 * ActivationDrawer -- Slide-out drawer accessible from the chat sidebar
 * that shows all available agents/skills/workflows and lets the user
 * toggle them on/off.
 *
 * Features:
 * - Tab bar: Agents | Skills | Workflows
 * - Grouped by scope (Organization > Team > Personal)
 * - Disabled entities shown greyed out with tooltip
 * - Search/filter within each tab
 * - Optimistic toggle with error rollback via ActivationToggle
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { X } from '@phosphor-icons/react';
import { cn } from '@/utils/cn';
import { Tabs, TabPanel } from '@/components/ui/Tabs';
import { SearchInput } from '@/components/ui/SearchInput';
import { Switch } from '@/components/ui/Switch';
import { Tooltip } from '@/components/ui/Tooltip';
import { useToast } from '@/components/ui/Toast';
import { SecondaryDataNotice } from '@/components/ui/SecondaryDataNotice';
import type { SecondaryQueryLike } from '@/hooks/useSecondaryQueryState';
import { useAgents, useActivateAgent, useDeactivateAgent } from '@/hooks/useAgentForm';
import { useSkills, useActivateSkill, useDeactivateSkill } from '@/hooks/useSkillForm';
import { useWorkflows, useActivateWorkflow, useDeactivateWorkflow } from '@/hooks/useWorkflowForm';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ActivationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

type EntityTab = 'agents' | 'skills' | 'workflows';

interface ToggleableEntity {
  id: string;
  name: string;
  scope: 'org' | 'team' | 'user';
  status: 'active' | 'disabled' | 'draft';
  is_activated_by_current_user: boolean;
  description: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const SCOPE_LABELS: Record<string, string> = {
  org: 'Organização',
  team: 'Equipe',
  user: 'Pessoal',
};

const SCOPE_ORDER: Record<string, number> = { org: 0, team: 1, user: 2 };

function groupByScope(entities: ToggleableEntity[]): Record<string, ToggleableEntity[]> {
  const groups: Record<string, ToggleableEntity[]> = {};
  for (const entity of entities) {
    const arr = groups[entity.scope];
    if (arr) {
      arr.push(entity);
    } else {
      groups[entity.scope] = [entity];
    }
  }
  return groups;
}

function sortedScopeKeys(groups: Record<string, ToggleableEntity[]>): string[] {
  return Object.keys(groups).sort((a, b) => (SCOPE_ORDER[a] ?? 99) - (SCOPE_ORDER[b] ?? 99));
}

// ---------------------------------------------------------------------------
// Entity list section
// ---------------------------------------------------------------------------

function EntitySection({
  entities,
  search,
  onActivate,
  onDeactivate,
  query,
  subject,
}: {
  entities: ToggleableEntity[];
  search: string;
  onActivate: (id: string) => Promise<void>;
  onDeactivate: (id: string) => Promise<void>;
  /** Catalog query: loading / failure shown inside the drawer (FEAT-052 TASK-010, B9). */
  query: SecondaryQueryLike & { isSuccess: boolean };
  subject: string;
}) {
  const filtered = useMemo(() => {
    if (!search) return entities;
    const lower = search.toLowerCase();
    return entities.filter((e) => e.name.toLowerCase().includes(lower));
  }, [entities, search]);

  const groups = useMemo(() => groupByScope(filtered), [filtered]);
  const scopeKeys = useMemo(() => sortedScopeKeys(groups), [groups]);

  if (!query.isSuccess) {
    return <SecondaryDataNotice query={query} subject={subject} variant="block" className="mt-2" />;
  }

  if (filtered.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-text-tertiary">
        Nenhum item encontrado.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {scopeKeys.map((scope) => (
        <div key={scope}>
          <h4 className="text-xs font-semibold text-text-tertiary uppercase tracking-wider px-1 mb-2">
            {SCOPE_LABELS[scope] ?? scope}
          </h4>
          <div className="flex flex-col gap-1">
            {(groups[scope] ?? []).map((entity) => (
              <EntityRow
                key={entity.id}
                entity={entity}
                onActivate={onActivate}
                onDeactivate={onDeactivate}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function EntityRow({
  entity,
  onActivate,
  onDeactivate,
}: {
  entity: ToggleableEntity;
  onActivate: (id: string) => Promise<void>;
  onDeactivate: (id: string) => Promise<void>;
}) {
  const { addToast } = useToast();
  const [optimistic, setOptimistic] = useState(entity.is_activated_by_current_user);
  const [isPending, setIsPending] = useState(false);

  // Sync when data refetches
  if (!isPending && optimistic !== entity.is_activated_by_current_user) {
    setOptimistic(entity.is_activated_by_current_user);
  }

  const isDisabled = entity.status !== 'active';

  const handleToggle = useCallback(
    async (newState: boolean) => {
      if (isDisabled) return;
      setOptimistic(newState);
      setIsPending(true);
      try {
        if (newState) {
          await onActivate(entity.id);
        } else {
          await onDeactivate(entity.id);
        }
      } catch {
        setOptimistic(!newState);
        addToast({
          variant: 'error',
          title: `Falha ao ${newState ? 'ativar' : 'desativar'} "${entity.name}"`,
        });
      } finally {
        setIsPending(false);
      }
    },
    [entity.id, entity.name, isDisabled, onActivate, onDeactivate, addToast],
  );

  const row = (
    <div
      className={cn(
        'flex items-center justify-between gap-3 px-3 py-2 rounded-lg',
        'transition-colors',
        isDisabled ? 'opacity-50' : 'hover:bg-bg-hover',
      )}
    >
      <div className="flex flex-col min-w-0">
        <span className={cn('text-sm font-medium truncate', isDisabled ? 'text-text-disabled' : 'text-text-primary')}>
          {entity.name}
        </span>
        {entity.description && (
          <span className="text-xs text-text-tertiary truncate">{entity.description}</span>
        )}
      </div>
      <Switch
        checked={optimistic}
        onChange={handleToggle}
        disabled={isDisabled || isPending}
        aria-label={`${optimistic ? 'Desativar' : 'Ativar'} ${entity.name}`}
      />
    </div>
  );

  if (isDisabled) {
    return (
      <Tooltip content={entity.status === 'disabled' ? 'Este item esta desativado' : 'Este item esta em rascunho'}>
        {row}
      </Tooltip>
    );
  }

  return row;
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const TAB_ITEMS = [
  { value: 'agents', label: 'Agentes' },
  { value: 'skills', label: 'Skills' },
  { value: 'workflows', label: 'Workflows' },
];

export function ActivationDrawer({ isOpen, onClose }: ActivationDrawerProps) {
  const [activeTab, setActiveTab] = useState<EntityTab>('agents');
  const [search, setSearch] = useState('');
  const drawerRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const intl = useIntl();

  // Data queries -- only while the drawer is open (FEAT-052 TASK-010, B9); cache is reused on reopen
  const agentsQuery = useAgents({ per_page: 100 }, { enabled: isOpen });
  const skillsQuery = useSkills({ per_page: 100 }, { enabled: isOpen });
  const workflowsQuery = useWorkflows({ per_page: 100 }, { enabled: isOpen });
  const agentsData = agentsQuery.data;
  const skillsData = skillsQuery.data;
  const workflowsData = workflowsQuery.data;
  const subjects: Record<EntityTab, string> = {
    agents: intl.formatMessage({
      id: 'common.secondaryData.subject.activationAgents',
      defaultMessage: 'a lista de agentes',
    }),
    skills: intl.formatMessage({
      id: 'common.secondaryData.subject.activationSkills',
      defaultMessage: 'a lista de skills',
    }),
    workflows: intl.formatMessage({
      id: 'common.secondaryData.subject.activationWorkflows',
      defaultMessage: 'a lista de workflows',
    }),
  };

  // Mutation hooks
  const activateAgent = useActivateAgent();
  const deactivateAgent = useDeactivateAgent();
  const activateSkill = useActivateSkill();
  const deactivateSkill = useDeactivateSkill();
  const activateWorkflow = useActivateWorkflow();
  const deactivateWorkflow = useDeactivateWorkflow();

  // Mutation callbacks
  const handleActivateAgent = useCallback(async (id: string) => {
    await activateAgent.mutateAsync(id);
  }, [activateAgent]);
  const handleDeactivateAgent = useCallback(async (id: string) => {
    await deactivateAgent.mutateAsync(id);
  }, [deactivateAgent]);
  const handleActivateSkill = useCallback(async (id: string) => {
    await activateSkill.mutateAsync(id);
  }, [activateSkill]);
  const handleDeactivateSkill = useCallback(async (id: string) => {
    await deactivateSkill.mutateAsync(id);
  }, [deactivateSkill]);
  const handleActivateWorkflow = useCallback(async (id: string) => {
    await activateWorkflow.mutateAsync(id);
  }, [activateWorkflow]);
  const handleDeactivateWorkflow = useCallback(async (id: string) => {
    await deactivateWorkflow.mutateAsync(id);
  }, [deactivateWorkflow]);

  // Map to ToggleableEntity
  const agents: ToggleableEntity[] = useMemo(
    () => (agentsData?.data ?? []).map((a) => ({
      id: a.id,
      name: a.name,
      scope: a.scope,
      status: a.status,
      is_activated_by_current_user: a.is_activated_by_current_user,
      description: a.description,
    })),
    [agentsData],
  );
  const skills: ToggleableEntity[] = useMemo(
    () => (skillsData?.data ?? []).map((s) => ({
      id: s.id,
      name: s.name,
      scope: s.scope,
      status: s.status,
      is_activated_by_current_user: s.is_activated_by_current_user,
      description: s.description,
    })),
    [skillsData],
  );
  const workflows: ToggleableEntity[] = useMemo(
    () => (workflowsData?.data ?? []).map((w) => ({
      id: w.id,
      name: w.name,
      scope: w.scope,
      status: w.status,
      is_activated_by_current_user: w.is_activated_by_current_user,
      description: w.description,
    })),
    [workflowsData],
  );

  // Focus management
  useEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement as HTMLElement;
    const timer = setTimeout(() => {
      drawerRef.current?.querySelector<HTMLElement>('button, input')?.focus();
    }, 50);
    return () => {
      clearTimeout(timer);
      previousFocusRef.current?.focus();
    };
  }, [isOpen]);

  // ESC to close + body scroll lock
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Reset search when tab changes
  useEffect(() => setSearch(''), [activeTab]);

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[var(--z-overlay)] bg-bg-overlay animate-[fade-in_200ms_ease-out]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Painel de ativacao"
        className={cn(
          'fixed top-0 right-0 bottom-0 z-[var(--z-modal)]',
          'w-full max-w-[360px] bg-bg-elevated border-l border-border-subtle shadow-xl',
          'flex flex-col',
          'animate-[slide-in-right_300ms_ease-out]',
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-border-subtle">
          <h2 className="text-lg font-semibold text-text-primary">Ativar / Desativar</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-text-tertiary hover:text-text-primary transition-colors cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus"
            aria-label="Fechar painel"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-4 pt-3">
          <Tabs
            items={TAB_ITEMS}
            value={activeTab}
            onChange={(v) => setActiveTab(v as EntityTab)}
            ariaLabel="Tipo de entidade"
            idPrefix="activation"
          />
        </div>

        {/* Search */}
        <div className="px-4 py-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Filtrar..."
            aria-label="Filtrar itens"
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          <TabPanel value="agents" activeValue={activeTab} idPrefix="activation">
            <EntitySection
              entities={agents}
              search={search}
              onActivate={handleActivateAgent}
              onDeactivate={handleDeactivateAgent}
              query={agentsQuery}
              subject={subjects.agents}
            />
          </TabPanel>
          <TabPanel value="skills" activeValue={activeTab} idPrefix="activation">
            <EntitySection
              entities={skills}
              search={search}
              onActivate={handleActivateSkill}
              onDeactivate={handleDeactivateSkill}
              query={skillsQuery}
              subject={subjects.skills}
            />
          </TabPanel>
          <TabPanel value="workflows" activeValue={activeTab} idPrefix="activation">
            <EntitySection
              entities={workflows}
              search={search}
              onActivate={handleActivateWorkflow}
              onDeactivate={handleDeactivateWorkflow}
              query={workflowsQuery}
              subject={subjects.workflows}
            />
          </TabPanel>
        </div>
      </div>
    </>,
    document.body,
  );
}
